import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { CATEGORIES } from "@/lib/data";

const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const IS_MOBILE = window.matchMedia("(max-width: 768px)").matches;

export const nodeStore = { screen: [], hoveredId: null };
const _v3 = new THREE.Vector3();

function fibonacciSphere(n, r) {
    const pts = new Float32Array(n * 3);
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < n; i++) {
        const y = 1 - (i / (n - 1)) * 2;
        const rad = Math.sqrt(Math.max(0, 1 - y * y));
        const th = golden * i;
        pts[i * 3] = Math.cos(th) * rad * r;
        pts[i * 3 + 1] = y * r;
        pts[i * 3 + 2] = Math.sin(th) * rad * r;
    }
    return pts;
}

function latLon(lat, lon, r) {
    const phi = ((90 - lat) * Math.PI) / 180;
    const theta = ((lon + 180) * Math.PI) / 180;
    return new THREE.Vector3(-r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.sin(theta));
}

const HUBS = [
    [40.7, -74.0],
    [51.5, -0.12],
    [1.35, 103.8],
    [35.68, 139.69],
    [47.37, 8.54],
    [25.2, 55.27],
].map(([la, lo]) => latLon(la, lo, 2.02));

function DotField({ count }) {
    const positions = useMemo(() => fibonacciSphere(count, 2.0), [count]);
    return (
        <points>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[positions, 3]} />
            </bufferGeometry>
            <pointsMaterial size={0.02} color="#4A9BFF" transparent opacity={1} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
        </points>
    );
}

function GoldField() {
    const positions = useMemo(() => fibonacciSphere(IS_MOBILE ? 120 : 260, 2.01), []);
    return (
        <points>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[positions, 3]} />
            </bufferGeometry>
            <pointsMaterial size={0.03} color="#D7B06A" transparent opacity={0.5} sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending} />
        </points>
    );
}

function Rings() {
    const rings = useMemo(() => {
        const list = [];
        for (const y of [-1.3, -0.65, 0, 0.65, 1.3]) {
            const r = Math.sqrt(Math.max(0.001, 4 - y * y));
            const pts = [];
            for (let i = 0; i <= 90; i++) {
                const a = (i / 90) * Math.PI * 2;
                pts.push(new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r));
            }
            list.push({ pts, rot: [0, 0, 0] });
        }
        for (let k = 0; k < 6; k++) {
            const pts = [];
            for (let i = 0; i <= 90; i++) {
                const a = (i / 90) * Math.PI;
                pts.push(new THREE.Vector3(Math.cos(a) * 2, Math.sin(a) * 2, 0));
            }
            list.push({ pts, rot: [0, (k * Math.PI) / 6, 0] });
        }
        return list;
    }, []);
    return rings.map((r, i) => (
        <group key={i} rotation={r.rot}>
            <lineLoop>
                <bufferGeometry>
                    <bufferAttribute attach="attributes-position" args={[new Float32Array(r.pts.flatMap((p) => [p.x, p.y, p.z])), 3]} />
                </bufferGeometry>
                <lineBasicMaterial color="#1687FF" transparent opacity={0.18} depthWrite={false} />
            </lineLoop>
        </group>
    ));
}

function Atmosphere() {
    const mat = useMemo(
        () =>
            new THREE.ShaderMaterial({
                vertexShader: "varying vec3 vN; void main(){ vN = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
                fragmentShader: "varying vec3 vN; void main(){ float f = pow(1.0 - abs(vN.z), 2.4); gl_FragColor = vec4(0.086, 0.53, 1.0, f * 0.6); }",
                transparent: true,
                side: THREE.BackSide,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
            }),
        []
    );
    return (
        <mesh material={mat}>
            <sphereGeometry args={[2.34, 48, 48]} />
        </mesh>
    );
}

function OrbitRing({ y }) {
    return (
        <mesh position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[2.9, 0.0035, 8, 120]} />
            <meshBasicMaterial color="#1687FF" transparent opacity={0.22} depthWrite={false} />
        </mesh>
    );
}

function Arc({ a, b, delay, speed, color }) {
    const { curve, geom } = useMemo(() => {
        const mid = a.clone().add(b).multiplyScalar(0.5);
        const lift = 1.18 + a.distanceTo(b) * 0.12;
        mid.normalize().multiplyScalar(2.02 * lift);
        const c = new THREE.QuadraticBezierCurve3(a.clone().multiplyScalar(1.01), mid, b.clone().multiplyScalar(1.01));
        return { curve: c, geom: new THREE.TubeGeometry(c, 48, 0.006, 6, false) };
    }, [a, b]);
    const comet = useRef();
    useFrame(({ clock }) => {
        if (!comet.current) return;
        const t = REDUCED ? 0.5 : (clock.elapsedTime * speed + delay) % 1;
        comet.current.position.copy(curve.getPoint(t));
    });
    return (
        <group>
            <mesh geometry={geom}>
                <meshBasicMaterial color={color} transparent opacity={0.22} depthWrite={false} blending={THREE.AdditiveBlending} />
            </mesh>
            <mesh ref={comet}>
                <sphereGeometry args={[0.028, 10, 10]} />
                <meshBasicMaterial color="#F3D18A" />
            </mesh>
        </group>
    );
}

function AssetNode({ cat, pos, selected, featured, onSelect }) {
    const [hover, setHover] = useState(false);
    const ref = useRef();
    const scale = useRef(new THREE.Vector3(1, 1, 1));
    const target = useMemo(() => new THREE.Vector3(), []);
    useFrame((_, d) => {
        if (!ref.current) return;
        const byCard = nodeStore.hoveredId === cat.id;
        const s = selected ? 1.8 : hover || byCard ? 1.55 : featured ? 1.35 : 1;
        target.set(s, s, s);
        scale.current.lerp(target, 0.18);
        ref.current.scale.copy(scale.current);
        if (!REDUCED) ref.current.rotation.y += d * 0.8;
    });
    const lit = selected || featured;
    return (
        <group position={pos}>
            <mesh
                ref={ref}
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect(cat.id);
                }}
                onPointerOver={(e) => {
                    e.stopPropagation();
                    setHover(true);
                }}
                onPointerOut={() => setHover(false)}
            >
                <octahedronGeometry args={[0.075, 0]} />
                <meshBasicMaterial color={lit ? "#F3D18A" : hover ? "#55B7FF" : "#3B8DFF"} wireframe={!lit && !hover} />
            </mesh>
        </group>
    );
}

function GlobeContent({ selectedId, featuredId, onSelect }) {
    const group = useRef();
    const { camera } = useThree();
    const pointer = useRef({ x: 0, y: 0 });

    const nodes = useMemo(
        () =>
            CATEGORIES.map((c, i) => {
                const th = (i / CATEGORIES.length) * Math.PI * 2 + 0.5;
                const y = i % 2 === 0 ? 0.85 : -0.6;
                const pos = new THREE.Vector3(Math.cos(th) * 2.9, y, Math.sin(th) * 2.9);
                return { cat: c, pos, angleY: Math.atan2(pos.x, pos.z) };
            }),
        []
    );

    useEffect(() => {
        const f = (e) => {
            pointer.current.x = (e.clientX / window.innerWidth) * 2 - 1;
            pointer.current.y = (e.clientY / window.innerHeight) * 2 - 1;
        };
        window.addEventListener("mousemove", f, { passive: true });
        return () => window.removeEventListener("mousemove", f);
    }, []);

    useFrame((_, d) => {
        const g = group.current;
        if (!g) return;
        if (selectedId && !REDUCED) {
            const n = nodes.find((nd) => nd.cat.id === selectedId);
            if (n) {
                const rot = g.rotation.y;
                let diff = (-n.angleY - rot) % (Math.PI * 2);
                if (diff > Math.PI) diff -= Math.PI * 2;
                if (diff < -Math.PI) diff += Math.PI * 2;
                g.rotation.y = rot + diff * Math.min(1, d * 2.5);
            }
        } else if (!REDUCED) {
            g.rotation.y += d * 0.05;
        }
        g.rotation.x = THREE.MathUtils.lerp(g.rotation.x, REDUCED ? 0.25 : 0.25 + pointer.current.y * 0.08, 0.06);
        camera.position.z = THREE.MathUtils.lerp(camera.position.z, selectedId ? 5.8 : 7.8, 0.06);

        nodes.forEach((n, i) => {
            _v3.copy(n.pos).applyMatrix4(g.matrixWorld);
            const wz = _v3.z;
            _v3.project(camera);
            const s = nodeStore.screen[i] || (nodeStore.screen[i] = {});
            s.id = n.cat.id;
            s.nx = (_v3.x + 1) / 2;
            s.ny = (1 - _v3.y) / 2;
            s.front = THREE.MathUtils.clamp((wz + 1.4) / 2.8, 0, 1);
        });
    });

    const arcs = useMemo(
        () =>
            [
                [0, 1],
                [1, 4],
                [4, 5],
                [5, 2],
                [2, 3],
                [3, 0],
            ].map(([a, b], i) => ({ a: HUBS[a], b: HUBS[b], delay: i * 0.35, speed: 0.12 + (i % 3) * 0.05, color: i % 3 === 0 ? "#F3D18A" : "#1687FF" })),
        []
    );

    return (
        <group ref={group}>
            <mesh>
                <sphereGeometry args={[1.97, 48, 48]} />
                <meshBasicMaterial color="#081a30" />
            </mesh>
            <DotField count={IS_MOBILE ? 900 : 1700} />
            <GoldField />
            <Rings />
            <Atmosphere />
            <OrbitRing y={0.85} />
            <OrbitRing y={-0.6} />
            {arcs.map((arc, i) => (
                <Arc key={i} {...arc} />
            ))}
            {nodes.map((n) => (
                <AssetNode key={n.cat.id} cat={n.cat} pos={n.pos} selected={selectedId === n.cat.id} featured={featuredId === n.cat.id} onSelect={onSelect} />
            ))}
            {HUBS.map((p, i) => (
                <mesh key={`hub-${i}`} position={p}>
                    <sphereGeometry args={[0.03, 8, 8]} />
                    <meshBasicMaterial color="#55B7FF" />
                </mesh>
            ))}
        </group>
    );
}

export default function Globe({ selectedId, featuredId, onSelect }) {
    return (
        <Canvas
            dpr={[1, IS_MOBILE ? 1.35 : 1.75]}
            camera={{ position: [0, 0.4, 7.8], fov: 42 }}
            gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
            style={{ background: "transparent" }}
            data-testid="hero-3d-globe-canvas"
        >
            <GlobeContent selectedId={selectedId} featuredId={featuredId} onSelect={onSelect} />
        </Canvas>
    );
}
